import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import logger from '../services/logger';
import { AdminCredentials } from '../types';

// Global state to prevent multiple auth initializations
let globalAuthState = {
  isAuthenticated: false,
  isLoading: true,
  initialized: false
};

let globalAuthListeners: Set<() => void> = new Set();

const notifyAuthStateChange = () => {
  globalAuthListeners.forEach(listener => listener());
};

const initializeAuthOnce = () => {
  if (globalAuthState.initialized) return;
  
  const token = localStorage.getItem('authToken');
  const refreshToken = localStorage.getItem('refreshToken');
  
  if (token) {
    apiService.setToken(token);
    globalAuthState.isAuthenticated = true;
    logger.debug('AUTH', 'Access token loaded from localStorage (global)');
  } else if (refreshToken) {
    // Try to refresh if we only have refresh token
    logger.debug('AUTH', 'Only refresh token found, attempting refresh (global)');
    apiService.refreshToken(refreshToken)
      .then((authData) => {
        globalAuthState.isAuthenticated = true;
        notifyAuthStateChange();
        logger.debug('AUTH', 'Token refreshed successfully during initialization');
      })
      .catch((error) => {
        logger.warn('AUTH', 'Failed to refresh token during initialization');
        localStorage.removeItem('refreshToken');
        globalAuthState.isAuthenticated = false;
        notifyAuthStateChange();
      });
  } else {
    logger.debug('AUTH', 'No tokens found in localStorage (global)');
  }
  
  globalAuthState.isLoading = false;
  globalAuthState.initialized = true;
  notifyAuthStateChange();
};

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(globalAuthState.isAuthenticated);
  const [isLoading, setIsLoading] = useState(globalAuthState.isLoading);
  
  // Initialize auth globally only once
  useEffect(() => {
    initializeAuthOnce();
    
    // Subscribe to global auth state changes
    const updateLocalState = () => {
      setIsAuthenticated(globalAuthState.isAuthenticated);
      setIsLoading(globalAuthState.isLoading);
    };
    
    globalAuthListeners.add(updateLocalState);
    updateLocalState(); // Sync initial state
    
    return () => {
      globalAuthListeners.delete(updateLocalState);
    };
  }, []);

  const login = useCallback(async (credentials: AdminCredentials) => {
    logger.authentication('login_attempt', { username: credentials.username });
    try {
      await apiService.login(credentials);
      globalAuthState.isAuthenticated = true;
      setIsAuthenticated(true);
      notifyAuthStateChange();
      logger.userAction('login_success');
      return { success: true };
    } catch (error: any) {
      logger.authentication('login_failure', { 
        username: credentials.username, 
        error: error.response?.data?.detail || error.message 
      });
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  }, []);

  const logout = useCallback(() => {
    logger.authentication('logout');
    logger.userAction('logout');
    apiService.clearToken();
    globalAuthState.isAuthenticated = false;
    globalAuthState.initialized = false; // Allow re-initialization
    setIsAuthenticated(false);
    notifyAuthStateChange();
  }, []);

  const validateToken = useCallback(async () => {
    if (!isAuthenticated) return false;
    
    try {
      await apiService.getSystemStatus();
      logger.debug('AUTH', 'Token validation successful');
      return true;
    } catch (error) {
      logger.warn('AUTH', 'Token validation failed, logging out');
      logout();
      return false;
    }
  }, [isAuthenticated, logout]);

  return {
    isAuthenticated,
    isLoading,
    login,
    logout,
    validateToken
  };
};